import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Grid,
  MenuItem,
  Autocomplete,
  Drawer,
  InputAdornment,
  Checkbox,
  Chip,
  Avatar,
  Stack,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import ListAltIcon from "@mui/icons-material/ListAlt";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import { TableNodata } from "../../components/TableNodata";
import { TableLoader } from "../../components/TableLoader";
import { TruncatedText } from "../../components/TruncatedText";
import CustomPagination from "../../components/CustomPagination";
import useDebounce from "../../hooks/useDebounce";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import api from "../../api/client";
import { useSnackbar } from "../../providers/SnackbarProvider";
import { OverAllLoader } from "../../components/OverAllLoader";
import { useChangeableItems, getLabelByKey, useOrganizations, useSelectedOrganizationId } from "../../store/useDynamicLabel";

const Programs = () => {

  const changeableItems = useChangeableItems();
  const getLabel = (itemKey) => getLabelByKey(changeableItems, itemKey);
  const organizations = useOrganizations();
  const selectedOrganizationId = useSelectedOrganizationId();
  const organizationType =
    organizations.find((org) => org.id === selectedOrganizationId)?.name ||
    localStorage.getItem("organization_name");
  const isCorporate = organizationType === "corporate";

  const tableDataCache = useRef({});
  const [programs, setPrograms] = useState([]);
  const [openForm, setOpenForm] = useState(false);
  const [editProgramId, setEditProgramId] = useState(null);
  const [overAllLoading, setOverAllLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [instituteNames, setInstituteNames] = useState([]);
  const [instituteDropdownList, setInstituteDropdownList] = useState([]);
  const [programDropdownList, setProgramDropdownList] = useState([]);
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const userRole = localStorage.getItem("role");
  const isSuperAdmin = userRole === "SUPER_ADMIN";
  const isInstituteAdmin = userRole === "INSTITUTE_ADMIN";

  // S.No + Program (name + code merged) + Display Name [+ Outcome for IA] + Status + Created + Actions
  const totalColumns = isSuperAdmin ? 6 : 5;

  // ===== Pagination =====
  const [page, setPage] = useState(+localStorage.getItem("currentPage") || 0);
  const [rowsPerPage] = useState(+localStorage.getItem("rowsPerPage") || 10);
  const [count, setCount] = useState(0);

  // ===== Filters — selected (in drawer, not yet applied) =====
  const [search, setSearch] = useState(localStorage.getItem("searchvalue") || "");
  const debouncedSearch = useDebounce(search, 400);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedInstitute, setSelectedInstitute] = useState(null);

  // ===== Filters — applied (driving the request) — restored from localStorage =====
  const [appliedProgram, setAppliedProgram] = useState(() => {
    const id = localStorage.getItem("programs_filter_programId");
    const name = localStorage.getItem("programs_filter_programLabel");
    const code = localStorage.getItem("programs_filter_programCodeChip");
    return id
      ? {
        program_id: parseInt(id),
        program_name: name || "",
        display_name: name || "",
        program_code: code || "",
      }
      : null;
  });
  const [appliedStatus, setAppliedStatus] = useState(
    localStorage.getItem("programs_filter_status") || ""
  );
  const [appliedInstitute, setAppliedInstitute] = useState(() => {
    const id = localStorage.getItem("programs_filter_instituteId");
    const name = localStorage.getItem("programs_filter_instituteName");
    return id ? { institute_id: parseInt(id), name: name || "" } : null;
  });

  useEffect(() => {
    localStorage.removeItem("programs_filter_code");
    localStorage.removeItem("programs_filter_displayName");
  }, []);

  const hasAppliedFilter =
    !!appliedProgram ||
    !!appliedStatus ||
    !!appliedInstitute;

  const outcomeLabel = isCorporate ? "Job Description" : "Program Outcome";

  const schema = isSuperAdmin
    ? Yup.object().shape({
      program_name: Yup.string().required(`${getLabel('PROGRAMS')} name is required`),
      program_code: Yup.string()
        .required(`${getLabel('PROGRAM_CODE')} is required`)
        .max(20, "Max 20 characters")
        .matches(/^[A-Za-z0-9]+$/, "Alphanumeric only"),
      display_name: Yup.string()
        .nullable()
        .transform((v) => (v === "" ? null : v))
        .max(200, "Max 200 characters"),
      institute_id: Yup.number()
        .nullable()
        .transform((v) => (Number.isNaN(v) || v === "" ? null : v)),
      // Mandatory; same rules as the Institute Admin outcome field
      programDesc: Yup.string()
        .trim()
        .required(`${outcomeLabel} is required.`)
        .test(
          "not-only-numbers",
          `${outcomeLabel} cannot contain only numbers.`,
          (value) => !value || !/^\d+$/.test(value)
        )
        .test(
          "not-only-special-characters",
          `${outcomeLabel} cannot contain only special characters.`,
          (value) => !value || /[A-Za-z0-9]/.test(value)
        ),
      status: Yup.string().required("Status is required"),
    })
    : Yup.object().shape({
      program_name: Yup.string()
        .trim()
        .required("Program name is required")
          .matches(/^[^0-9]*$/, "Program name must contain only valid alphabetic characters.")
        .matches(
          /^[A-Za-z]+(?:[ .'-]+[A-Za-z]+)*\.?$/,
          "Program name must contain valid alphabetic characters."
        )
        .min(3, "Program name must be at least 3 characters long.")
        .max(50, "Program name cannot exceed 50 characters."),
      program_code: Yup.string()
        .required(`${getLabel('PROGRAM_CODE')} is required`)
        .min(3, `${getLabel('PROGRAM_CODE')} must be at least 3 characters long.`)
        .max(20, "Max 20 characters")
        .matches(/^[A-Za-z0-9]+$/, "Alphanumeric only")
        .test(
          "not-only-numbers",
          "cannot contain only numbers.",
          (value) => !value || !/^\d+$/.test(value.trim())
        ),
      display_name: Yup.string()
        .nullable()
        .transform((v) => (v === "" ? null : v))
        .max(100, "Display name cannot exceed 100 characters.")
        .test(
          "not-only-numbers",
          "Display name cannot contain only numbers.",
          (value) => !value || !/^\d+$/.test(value.trim())
        )
        .test(
          "not-only-special-characters",
          "Display name cannot contain only special characters.",
          (value) => !value || /[A-Za-z0-9]/.test(value)
        ),
      programDesc: Yup.string()
        .trim()
        .required("Program outcome is required.")
        .test(
          "not-only-numbers",
          "Program outcome cannot contain only numbers.",
          (value) => !value || !/^\d+$/.test(value)
        )
        .test(
          "not-only-special-characters",
          "Program outcome cannot contain only special characters.",
          (value) => !value || /[A-Za-z0-9]/.test(value)
        ),
      status: Yup.string().required("Status is required"),
    });

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    mode: "onChange",
    defaultValues: {
      program_id: null,
      program_name: "",
      program_code: "",
      display_name: "",
      institute_id: null,
      programDesc: "",
      status: "ACTIVE",
      assignTrainer: [],
    },
  });

  const [trainersList, setTrainersList] = useState([]);
  // Assigned trainer names come with each ListPrograms row (same as the Subject list's assignedTrainersName)
  const getRowTrainers = (item) => item?.assignedTrainersName || item?.trainer_names || [];

  const withMandatoryTrainer = (ids = [], list = trainersList) => {
    const mandatoryId = list[0]?.trainer_id;
    return mandatoryId != null && !ids.includes(mandatoryId) ? [mandatoryId, ...ids] : ids;
  };

  const fetchTrainersList = async () => {
    try {
      const userStr = localStorage.getItem("userData");
      const user = userStr ? JSON.parse(userStr) : null;
      const res = await api.post("/ListTrainersForFilter", { institute_id: user?.institute_id || 0 });
      if (res?.data?.status === "success") {
        const list = res?.data?.data || [];
        setTrainersList(list);
        setValue("assignTrainer", withMandatoryTrainer(getValues("assignTrainer") || [], list));
      }
    } catch (e) {
      console.error("Error fetching trainers:", e);
    }
  };

  const fetchTrainersByProgram = async (programId) => {
    try {
      const res = await api.post("/GetTrainersByProgram", { program_id: programId });
      if (res?.data?.status === "success") {
        return res?.data?.data || [];
      }
      return [];
    } catch (e) {
      console.error("Error fetching trainers by program:", e);
      return [];
    }
  };

  useEffect(() => {
    if (isInstituteAdmin) fetchTrainersList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchInstituteNames = async () => {
    try {
      const { data } = await api.post("ListInstituteNames");
      setInstituteNames(data?.data ?? []);
    } catch (e) {
      console.error("Error fetching institutes:", e);
    }
  };

  const fetchInstitutesDropdown = async () => {
    try {
      const res = await api.post("/GetInstitutesDropdown", {});
      if (res?.data?.status === "success" || res?.data?.data) {
        setInstituteDropdownList(res?.data?.data || []);
      }
    } catch (e) {
      console.error("Error fetching institutes dropdown:", e);
    }
  };

  const fetchProgramsDropdown = async () => {
    try {
      const res = await api.post("/GetProgramsDropdown", {});
      if (res?.data?.status === "success") {
        setProgramDropdownList(res?.data?.data || []);
      }
    } catch (e) {
      console.error("Error fetching programs dropdown:", e);
    }
  };

  const getProgramsList = async () => {
    setLoading(true);

    const cacheKey = `p_${page}_s_${rowsPerPage}_q_${debouncedSearch}_pid_${appliedProgram?.program_id || ""}_st_${appliedStatus}_inst_${appliedInstitute?.institute_id || ""}`;

    if (tableDataCache.current[cacheKey]) {
      const cached = tableDataCache.current[cacheKey];
      setPrograms(cached.data);
      setCount(cached.total);
      setLoading(false);
      return;
    }

    try {
      const payload = {
        page: page || 0,
        size: rowsPerPage,
        ...(debouncedSearch ? { search_key: debouncedSearch } : {}),
        ...(appliedProgram?.program_id ? { program_id: appliedProgram.program_id } : {}),
        ...(appliedStatus ? { status: appliedStatus } : {}),
        ...(isSuperAdmin && appliedInstitute?.institute_id
          ? { institute_id: appliedInstitute.institute_id }
          : {}),
      };
      const res = await api.post("/ListPrograms", payload);
      if (res?.data?.status === "success") {
        const data = res.data.data || [];
        const total = res.data.total ?? data.length;
        setPrograms(data);
        setCount(total);
        tableDataCache.current[cacheKey] = { data, total };
      }
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getProgramsList();
  }, [
    page,
    debouncedSearch,
    appliedProgram,
    appliedStatus,
    appliedInstitute,
  ]);

  useEffect(() => {
    fetchProgramsDropdown();
    if (isSuperAdmin) {
      fetchInstituteNames();
      fetchInstitutesDropdown();
    }
  }, []);

  // Persist pagination
  useEffect(() => {
    localStorage.setItem("currentPage", page);
    localStorage.setItem("rowsPerPage", rowsPerPage);
  }, [page, rowsPerPage]);

  // Reset to page 0 whenever filters change
  useEffect(() => {
    setPage(0);
    localStorage.setItem("currentPage", 0);
  }, [
    debouncedSearch,
    appliedProgram,
    appliedStatus,
    appliedInstitute,
  ]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    localStorage.setItem("searchvalue", value);
  };

  const writeProgramFilterToStorage = (program) => {
    if (program?.program_id) {
      localStorage.setItem("programs_filter_programId", program.program_id);
      localStorage.setItem(
        "programs_filter_programLabel",
        program.display_name || program.program_name || ""
      );
      localStorage.setItem(
        "programs_filter_programCodeChip",
        program.program_code || ""
      );
    } else {
      localStorage.removeItem("programs_filter_programId");
      localStorage.removeItem("programs_filter_programLabel");
      localStorage.removeItem("programs_filter_programCodeChip");
    }
  };

  const writeInstituteFilterToStorage = (institute) => {
    if (institute?.institute_id) {
      localStorage.setItem("programs_filter_instituteId", institute.institute_id);
      localStorage.setItem("programs_filter_instituteName", institute.name || "");
    } else {
      localStorage.removeItem("programs_filter_instituteId");
      localStorage.removeItem("programs_filter_instituteName");
    }
  };

  const writeFilterStringToStorage = (key, value) => {
    if (value) localStorage.setItem(key, value);
    else localStorage.removeItem(key);
  };

  const handleApplyFilters = () => {
    tableDataCache.current = {};

    setAppliedProgram(selectedProgram);
    setAppliedStatus(selectedStatus);
    setAppliedInstitute(selectedInstitute);

    writeProgramFilterToStorage(selectedProgram);
    writeFilterStringToStorage("programs_filter_status", selectedStatus);
    writeInstituteFilterToStorage(selectedInstitute);

    setOpenDrawer(false);
  };

  const handleClearAllFilters = () => {
    tableDataCache.current = {};
    setSelectedProgram(null);
    setSelectedStatus("");
    setSelectedInstitute(null);
    setAppliedProgram(null);
    setAppliedStatus("");
    setAppliedInstitute(null);

    writeProgramFilterToStorage(null);
    writeFilterStringToStorage("programs_filter_status", "");
    writeInstituteFilterToStorage(null);
  };

  const handleAdd = () => {
    setEditProgramId(null);
    reset({
      program_id: null,
      program_name: "",
      program_code: "",
      display_name: "",
      institute_id: null,
      programDesc: "",
      status: "ACTIVE",
      assignTrainer: withMandatoryTrainer([]),
    });
    setOpenForm(true);
  };

  const handleEdit = async (id) => {
    setOverAllLoading(true);
    try {
      const res = await api.post("/ViewProgramByID", { program_id: id });
      if (res?.data?.status === "success") {
        const data = res.data.data;
        reset({
          program_id: data.program_id || id,
          program_name: data.program_name || "",
          program_code: data.program_code || "",
          display_name: data.display_name || "",
          institute_id: data.institute_id || null,
          programDesc: data.program_outcome || "",
          status: data.status || "ACTIVE",
          assignTrainer: withMandatoryTrainer(
            isInstituteAdmin ? (await fetchTrainersByProgram(id)).map((t) => t.user_id) : []
          ),
        });
        setEditProgramId(id);
        setOpenForm(true);
      } else {
        showSnackbar("error", res?.data?.message || "Failed to fetch program");
      }
    } catch (e) {
      showSnackbar("error", e?.response?.data?.message || "Failed to fetch program details");
    } finally {
      setOverAllLoading(false);
    }
  };

  const handleCloseForm = () => {
    setOpenForm(false);
  };

  const onSubmit = async (data) => {
    setOverAllLoading(true);
    try {
      let payload;
      if (isSuperAdmin) {
        payload = {
          program_name: data.program_name,
          program_code: data.program_code,
          status: data.status,
          ...(data.display_name ? { display_name: data.display_name } : {}),
          ...(data.institute_id ? { institute_id: data.institute_id } : {}),
          program_outcome: data.programDesc,
          ...(editProgramId && { program_id: editProgramId }),
        };
      } else {
        // Institute Admin — creates a program scoped to their institute (JWT).
        // Never sends institute_id (backend resolves from JWT).
        payload = {
          program_name: data.program_name,
          program_code: data.program_code,
          display_name: data.display_name,
          status: data.status,
          ...(data.programDesc ? { program_outcome: data.programDesc } : {}),
          trainersIds: data.assignTrainer || [],
          ...(editProgramId && { program_id: editProgramId }),
        };
      }

      const endpoint = editProgramId ? "/UpdateProgramByID" : "/CreateProgram";

      const res = await api.post(endpoint, payload);
      if (res?.data?.status === "success") {
        showSnackbar("success", res.data.message);
        setOpenForm(false);
        tableDataCache.current = {};
        getProgramsList();
        fetchProgramsDropdown();
      } else {
        showSnackbar("error", res?.data?.message || "Operation failed");
      }
    } catch (e) {
      showSnackbar("error", e?.response?.data?.message || "Something went wrong.");
    } finally {
      setOverAllLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", p: 1 }}>
      {overAllLoading && <OverAllLoader message="Saving program..." />}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
        sx={{ pb: 2 }}
      >
        <Grid
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: 0.5,
            alignItems: "center",
          }}
        >
          <Box className="icon-box">
            <ListAltIcon className="iconHeading" />
          </Box>
          <Typography sx={{ fontSize: { xs: 20, sm: 28 }, fontWeight: 700, color: "#0a0a0a" }}>
            {getLabel('PROGRAMS')}
          </Typography>
        </Grid>
        <Box display="flex" gap={4} alignItems="center">
          <TextField
            value={search}
            onChange={handleSearchChange}
            size="small"
            variant="outlined"
            placeholder="Search by name, code, display name"
            className="custom-search-field"
            InputProps={{
              startAdornment: (
                <InputAdornment
                  position="start"
                  sx={{ marginLeft: "6px", marginRight: "2px" }}
                >
                  <SearchIcon sx={{ color: "inherit", fontSize: "18px" }} />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAdd}
            className="gradient-button"
          >
            Add {getLabel('PROGRAMS')}
          </Button>
        </Box>
      </Box>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            {/* Filter row */}
            <TableRow>
              <TableCell colSpan={totalColumns}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    width: "100%",
                  }}
                >
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {appliedProgram && (
                      <Box className="filter-chip">
                        <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>
                          {appliedProgram.display_name || appliedProgram.program_name}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedProgram(null);
                            setAppliedProgram(null);
                            writeProgramFilterToStorage(null);
                          }}
                          sx={{ ml: 0.5 }}
                        >
                          <CloseIcon sx={{ fontSize: "14px" }} />
                        </IconButton>
                      </Box>
                    )}
                    {appliedStatus && (
                      <Box className="filter-chip">
                        <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>
                          {appliedStatus}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedStatus("");
                            setAppliedStatus("");
                            writeFilterStringToStorage("programs_filter_status", "");
                          }}
                          sx={{ ml: 0.5 }}
                        >
                          <CloseIcon sx={{ fontSize: "14px" }} />
                        </IconButton>
                      </Box>
                    )}
                    {isSuperAdmin && appliedInstitute && (
                      <Box className="filter-chip">
                        <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>
                          {appliedInstitute.name}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedInstitute(null);
                            setAppliedInstitute(null);
                            writeInstituteFilterToStorage(null);
                          }}
                          sx={{ ml: 0.5 }}
                        >
                          <CloseIcon sx={{ fontSize: "14px" }} />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <IconButton
                      onClick={() => {
                        setSelectedProgram(appliedProgram);
                        setSelectedStatus(appliedStatus);
                        setSelectedInstitute(appliedInstitute);
                        setOpenDrawer(true);
                      }}
                      color="primary"
                      className="more-filter-btn"
                    >
                      <FilterListIcon />
                      <Typography className="more-filter-text">
                        More filter
                      </Typography>
                    </IconButton>
                    {hasAppliedFilter && (
                      <Button
                        variant="outlined"
                        className="clear-filter-btn"
                        onClick={handleClearAllFilters}
                      >
                        Clear Filter
                      </Button>
                    )}
                  </Box>
                </Box>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell align="center" className="tableHeader"><b>S.No</b></TableCell>
              <TableCell align="left" className="tableHeader"><b>{getLabel('PROGRAM_CODE')} / {getLabel('PROGRAMS')}</b></TableCell>
              {isSuperAdmin && (
                <TableCell align="left" className="tableHeader"><b>{getLabel('INSTITUTE')}</b></TableCell>
              )}
              <TableCell align="left" className="tableHeader"><b>Assigned {getLabel('TRAINER')}</b></TableCell>
              <TableCell align="left" className="tableHeader"><b>Status</b></TableCell>
              <TableCell align="center" className="tableHeader" sx={{ width: "1%", whiteSpace: "nowrap" }}><b>Actions</b></TableCell>
            </TableRow>
          </TableHead>
          <TableBody className="tablebody">
            {loading ? (
              <TableRow>
                <TableCell colSpan={totalColumns} align="center">
                  <TableLoader message="Fetching programs..." />
                </TableCell>
              </TableRow>
            ) : programs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={totalColumns} align="center">
                  <TableNodata />
                </TableCell>
              </TableRow>
            ) : (
              programs.map((item, index) => (
                <TableRow
                  key={item.program_id}
                  sx={{
                    backgroundColor: index % 2 === 1 ? "#FAFAFA" : "inherit",
                    "&:hover": { backgroundColor: "#f1f1f1" },
                  }}
                >
                  <TableCell align="center">{page * rowsPerPage + index + 1}</TableCell>
                  <TableCell align="left">
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.2 }}>
                      {item.program_code && (
                        <TruncatedText
                          text={item.program_code}
                          maxWidth={300}
                          sx={{ fontWeight: 700, fontSize: '12px', color: '#1c97e3', letterSpacing: '0.3px' }}
                        />
                      )}
                      <TruncatedText
                        text={item.program_name || "-"}
                        maxWidth={300}
                        sx={{ fontWeight: 600, fontSize: '13px', color: '#1a1a1a', lineHeight: 1.3 }}
                      />
                    </Box>
                  </TableCell>
                  {isSuperAdmin && (
                    <TableCell align="left">
                      <TruncatedText
                        text={item.institute_name || "-"}
                        maxWidth={220}
                        sx={{ fontSize: "14px", color: "#666" }}
                      />
                    </TableCell>
                  )}
                  <TableCell align="left" sx={{ verticalAlign: "middle" }}>
                      {getRowTrainers(item).length > 0 ? (
                        <Box sx={{ display: "flex" }}>
                          {getRowTrainers(item).slice(0, 4).map((name, idx) => (
                            <Tooltip
                              key={idx}
                              arrow
                              title={
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                  sx={{
                                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                    borderRadius: 2,
                                    p: "6px 10px",
                                    boxShadow: "0 4px 10px rgba(0,0,0,0.3)",
                                  }}
                                >
                                  <Avatar
                                    sx={{
                                      width: 28,
                                      height: 28,
                                      fontSize: 13,
                                      background: "linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)",
                                      color: "#fff",
                                      fontWeight: 600,
                                    }}
                                  >
                                    {name.charAt(0).toUpperCase()}
                                  </Avatar>
                                  <Typography
                                    sx={{
                                      color: "#fff",
                                      fontSize: 13,
                                      fontWeight: 500,
                                      textShadow: "0px 1px 2px rgba(0,0,0,0.4)",
                                    }}
                                  >
                                    {name}
                                  </Typography>
                                </Stack>
                              }
                              componentsProps={{
                                tooltip: { sx: { background: "transparent", p: 0 } },
                                arrow: { sx: { color: "transparent" } },
                              }}
                            >
                              <Avatar
                                sx={{
                                  width: 30,
                                  height: 30,
                                  fontSize: 13,
                                  background: "linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)",
                                  color: "#fff",
                                  fontWeight: 600,
                                  boxShadow: "0 2px 4px rgba(0,0,0,0.3)",
                                  cursor: "pointer",
                                  transition: "transform 0.2s ease",
                                  "&:hover": { transform: "scale(1.1)", zIndex: 5 },
                                  ml: idx === 0 ? 0 : -1.0,
                                  border: "2px solid #fff",
                                }}
                              >
                                {name.charAt(0).toUpperCase()}
                              </Avatar>
                            </Tooltip>
                          ))}
                          {getRowTrainers(item).length > 4 && (
                            <Tooltip arrow title={getRowTrainers(item).slice(4).join(", ")}>
                              <Avatar
                                sx={{
                                  width: 30,
                                  height: 30,
                                  fontSize: 12,
                                  backgroundColor: "#e2e8f0",
                                  color: "#334155",
                                  fontWeight: 600,
                                  boxShadow: "0 2px 4px rgba(0,0,0,0.3)",
                                  cursor: "pointer",
                                  ml: -1.0,
                                  border: "2px solid #fff",
                                }}
                              >
                                +{getRowTrainers(item).length - 4}
                              </Avatar>
                            </Tooltip>
                          )}
                        </Box>
                      ) : (
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#888", fontStyle: "italic" }}>
                          Not Assigned Yet
                        </Typography>
                      )}
                    </TableCell>
                  <TableCell align="left">
                    <Box
                      sx={{
                        display: "inline-block",
                        px: 2,
                        py: 0.5,
                        borderRadius: "12px",
                        fontWeight: "bold",
                        fontSize: "12px",
                        backgroundColor: item.status === "ACTIVE" ? "#d4edda" : "#f8d7da",
                        color: item.status === "ACTIVE" ? "#155724" : "#721c24",
                        textAlign: "center",
                      }}
                    >
                      {item.status}
                    </Box>
                  </TableCell>
                  <TableCell align="center" sx={{ width: "1%", whiteSpace: "nowrap" }}>
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      gap={2}
                    >
                      <IconButton color="info" onClick={() => navigate('/viewProgram', { state: { program_id: item.program_id } })}>
                        <VisibilityIcon />
                        <Typography color="info" sx={{ fontSize: "12px", ml: "4px" }}>View</Typography>
                      </IconButton>
                      <IconButton color="primary" onClick={() => handleEdit(item.program_id)}>
                        <EditIcon />
                        <Typography color="primary" sx={{ fontSize: "12px", ml: "4px" }}>Edit</Typography>
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}

            {!loading && programs.length > 0 && (
              <CustomPagination
                page={page}
                setPage={setPage}
                count={count}
                rowsPerPage={rowsPerPage}
                colSpan={totalColumns}
              />
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Filter Drawer */}
      <Drawer
        anchor="right"
        open={openDrawer}
        onClose={() => setOpenDrawer(false)}
        PaperProps={{
          sx: {
            width: 360,
            p: 3,
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
            height: "100vh",
            borderRadius: "12px 0 0 12px",
          },
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <FilterListIcon color="primary" />
            <Typography variant="h6" fontWeight={600} color="primary">
              Filters
            </Typography>
          </Box>
          <IconButton aria-label="Close filters" size="small" onClick={() => setOpenDrawer(false)}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider />

        {/* Program Name dropdown — exact match */}
        <Box display="flex" flexDirection="column" gap={0.8}>
          <Typography variant="subtitle2" fontWeight={600}>
            {getLabel('PROGRAMS')} Name
          </Typography>
          <Autocomplete
            size="small"
            options={programDropdownList}
            getOptionLabel={(option) => {
              if (!option) return "";
              const label = option.display_name || option.program_name || "";
              return option.program_code ? `[${option.program_code}] ${label}` : label;
            }}
            isOptionEqualToValue={(o, v) => o?.program_id === v?.program_id}
            value={selectedProgram}
            onChange={(_, newValue) => setSelectedProgram(newValue)}
            renderOption={(props, option) => (
              <li {...props} key={option.program_id}>
                {option.program_code ? `[${option.program_code}] ` : ""}
                {option.display_name || option.program_name}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={`All ${getLabel('PROGRAMS')}`}
                fullWidth
                variant="outlined"
              />
            )}
          />
        </Box>

        {/* Status dropdown */}
        <Box display="flex" flexDirection="column" gap={0.8}>
          <Typography variant="subtitle2" fontWeight={600}>
            Status
          </Typography>
          <TextField
            select
            size="small"
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            fullWidth
            variant="outlined"
            SelectProps={{
              displayEmpty: true,
              renderValue: (value) =>
                value ? (
                  value === "ACTIVE" ? "Active" : "Inactive"
                ) : (
                  <span style={{ color: "#9e9e9e" }}>Select status</span>
                ),
            }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="ACTIVE">Active</MenuItem>
            <MenuItem value="INACTIVE">Inactive</MenuItem>
          </TextField>
        </Box>

        {/* Institute dropdown — SUPER_ADMIN only */}
        {isSuperAdmin && (
          <Box display="flex" flexDirection="column" gap={0.8}>
            <Typography variant="subtitle2" fontWeight={600}>
              {`${getLabel('INSTITUTE')}`}
            </Typography>
            <Autocomplete
              size="small"
              options={instituteDropdownList}
              getOptionLabel={(option) => option?.name || ""}
              isOptionEqualToValue={(o, v) => o?.institute_id === v?.institute_id}
              value={selectedInstitute}
              onChange={(_, newValue) => setSelectedInstitute(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={`All ${getLabel('INSTITUTE')}s`}
                  fullWidth
                  variant="outlined"
                />
              )}
            />
          </Box>
        )}

        <Button
          variant="contained"
          color="primary"
          fullWidth
          className="customButton"
          onClick={handleApplyFilters}
          sx={{ mt: "auto" }}
        >
          Apply Filters
        </Button>
      </Drawer>

      {/* Dialog */}
      <Dialog
        open={openForm}
        PaperProps={{
          sx: { width: "500px", borderRadius: "12px", p: 2 },
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
          <ListAltIcon
            sx={{
              backgroundColor: "#1976d2",
              color: "white",
              fontSize: "30px",
              borderRadius: "50%",
              padding: "5px",
            }}
          />
          <DialogTitle sx={{ fontWeight: 700, color: "#1976d2", padding: "5px" }}>
            {editProgramId ? `Update ${getLabel('PROGRAMS')}` : `Add ${getLabel('PROGRAMS')}`}
          </DialogTitle>
        </Box>
        <Divider sx={{ mt: 2, borderColor: "black", borderBottomWidth: 1, mx: 0 }} />
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent>
            <Box sx={{ mt: 1 }}>
              {isSuperAdmin && (
                <>
                  <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", mb: 1 }}>
                    <Typography sx={{ fontWeight: "bold" }}>
                      {getLabel('PROGRAMS')} Name <span style={{ color: "red" }}>*</span>
                    </Typography>
                  </Box>
                  <Controller
                    name="program_name"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder={isCorporate ? "e.g. Senior Frontend Software Developer" : `Enter ${getLabel('PROGRAMS')} name`}
                        variant="outlined"
                        inputProps={{ style: { textTransform: "uppercase" } }}
                        onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                        error={!!errors.program_name}
                        helperText={errors.program_name?.message}
                        sx={{ mb: 2 }}
                      />
                    )}
                  />

                  <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", mb: 1 }}>
                    <Typography sx={{ fontWeight: "bold" }}>
                      {getLabel('PROGRAM_CODE')} <span style={{ color: "red" }}>*</span>
                    </Typography>
                  </Box>
                  <Controller
                    name="program_code"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder={isCorporate ? "e.g. SFD001" : "e.g. 23CSE (alphanumeric, max 20)"}
                        variant="outlined"
                        inputProps={{ maxLength: 20, style: { textTransform: "uppercase" } }}
                        onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                        error={!!errors.program_code}
                        helperText={errors.program_code?.message}
                        sx={{ mb: 2 }}
                      />
                    )}
                  />

                  <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", mb: 1 }}>
                    <Typography sx={{ fontWeight: "bold" }}>
                      Display Name
                    </Typography>
                  </Box>
                  <Controller
                    name="display_name"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder={isCorporate ? "e.g. Senior Frontend Dev" : "e.g. CSE 2023 Batch (optional)"}
                        variant="outlined"
                        inputProps={{ maxLength: 200 }}
                        error={!!errors.display_name}
                        helperText={errors.display_name?.message}
                        sx={{ mb: 2 }}
                      />
                    )}
                  />

                  <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", mb: 1 }}>
                    <Typography sx={{ fontWeight: "bold" }}>
                      Assign {getLabel('INSTITUTE')}
                    </Typography>
                  </Box>
                  <Controller
                    name="institute_id"
                    control={control}
                    render={({ field }) => (
                      <Autocomplete
                        options={instituteNames}
                        getOptionLabel={(option) => option?.name || ""}
                        value={
                          instituteNames.find(
                            (inst) => inst.institute_id === field.value
                          ) || null
                        }
                        onChange={(_, newValue) =>
                          field.onChange(newValue ? newValue.institute_id : null)
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            placeholder={isCorporate ? `Select ${getLabel('INSTITUTE')} (optional)` : "Select institute (optional)"}
                            variant="outlined"
                            error={!!errors.institute_id}
                            helperText={errors.institute_id?.message}
                            sx={{ mb: 2 }}
                          />
                        )}
                      />
                    )}
                  />

                  <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", mb: 1 }}>
                    <Typography sx={{ fontWeight: "bold" }}>
                      {outcomeLabel} <span style={{ color: "red" }}>*</span>
                    </Typography>
                  </Box>
                  <Controller
                    name="programDesc"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        multiline
                        minRows={4}
                        placeholder={`Enter ${outcomeLabel} details...`}
                        variant="outlined"
                        error={!!errors.programDesc}
                        helperText={errors.programDesc?.message}
                        sx={{ mb: 2 }}
                      />
                    )}
                  />
                </>
              )}

              {isInstituteAdmin && (
                <>
                  <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", mb: 1 }}>
                    <Typography sx={{ fontWeight: "bold" }}>
                      {getLabel('PROGRAMS')} <span style={{ color: "red" }}>*</span>
                    </Typography>
                  </Box>
                  <Controller
                    name="program_name"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder={`Enter ${getLabel('PROGRAMS')} name`}
                        variant="outlined"
                        inputProps={{ style: { textTransform: "uppercase" } }}
                        onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                        error={!!errors.program_name}
                        helperText={errors.program_name?.message}
                        sx={{ mb: 2 }}
                      />
                    )}
                  />

                  <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", mb: 1 }}>
                    <Typography sx={{ fontWeight: "bold" }}>
                      {getLabel('PROGRAM_CODE')} <span style={{ color: "red" }}>*</span>
                    </Typography>
                  </Box>
                  <Controller
                    name="program_code"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="e.g. 23CSE (alphanumeric, max 20)"
                        variant="outlined"
                        inputProps={{ maxLength: 20, style: { textTransform: "uppercase" } }}
                        onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                        error={!!errors.program_code}
                        helperText={errors.program_code?.message}
                        sx={{ mb: 2 }}
                      />
                    )}
                  />

                  <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", mb: 1 }}>
                    <Typography sx={{ fontWeight: "bold" }}>
                      Display Name
                    </Typography>
                  </Box>
                  <Controller
                    name="display_name"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        placeholder="e.g. CSE 2023 Batch (optional)"
                        variant="outlined"
                        // inputProps={{ maxLength: 200 }}
                        error={!!errors.display_name}
                        helperText={errors.display_name?.message}
                        sx={{ mb: 2 }}
                      />
                    )}
                  />
                </>
              )}

              {isInstituteAdmin && (
                <>
                  <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", mb: 1 }}>
                    <Typography sx={{ fontWeight: "bold" }}>
                      Program Outcome <span style={{ color: "red" }}>*</span>
                    </Typography>
                  </Box>
                  <Controller
                    name="programDesc"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        multiline
                        minRows={4}
                        placeholder="Enter Program Outcome details..."
                        variant="outlined"
                        error={!!errors.programDesc}
                        helperText={errors.programDesc?.message}
                        sx={{ mb: 2 }}
                      />
                    )}
                  />
                </>
              )}

              {isInstituteAdmin && (
                <>
                  <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", mb: 1 }}>
                    <Typography sx={{ fontWeight: "bold" }}>
                      Assign {getLabel('TRAINER')}
                    </Typography>
                  </Box>
                  <Controller
                    name="assignTrainer"
                    control={control}
                    render={({ field }) => {
                      const mandatoryTrainerId = trainersList[0]?.trainer_id;
                      return (
                        <Autocomplete
                          multiple
                          disableCloseOnSelect
                          options={trainersList}
                          getOptionLabel={(option) => option.name || ""}
                          value={trainersList.filter((t) => field.value?.includes(t.trainer_id))}
                          onChange={(_, selectedValues) =>
                            field.onChange(withMandatoryTrainer(selectedValues.map((v) => v.trainer_id)))
                          }
                          isOptionEqualToValue={(option, value) => option.trainer_id === value.trainer_id}
                          renderOption={(props, option, { selected }) => {
                            const isMandatory = option.trainer_id === mandatoryTrainerId;
                            return (
                              <li
                                {...props}
                                aria-disabled={isMandatory}
                                style={{ ...(props.style || {}), ...(isMandatory ? { pointerEvents: "none" } : {}) }}
                              >
                                <Checkbox checked={selected || isMandatory} disabled={isMandatory} readOnly sx={{ mr: 1 }} />
                                {option.name}
                              </li>
                            );
                          }}
                          renderTags={(value, getTagProps) =>
                            value.map((option, index) => {
                              const isMandatory = option.trainer_id === mandatoryTrainerId;
                              const { key, ...tagProps } = getTagProps({ index });
                              return (
                                <Chip
                                  {...tagProps}
                                  key={option.trainer_id}
                                  label={option.name}
                                  size="small"
                                  onDelete={isMandatory ? undefined : tagProps.onDelete}
                                />
                              );
                            })
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder={`Select ${getLabel('TRAINER')}s`}
                              fullWidth
                              sx={{ mb: 2 }}
                            />
                          )}
                        />
                      );
                    }}
                  />
                </>
              )}

              {editProgramId && (
                <>
                  <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", mb: 1 }}>
                    <Typography sx={{ fontWeight: "bold" }}>
                      Status <span style={{ color: "red" }}>*</span>
                    </Typography>
                  </Box>
                  <Controller
                    name="status"
                    control={control}
                    render={({ field }) => (
                      <TextField {...field} select fullWidth error={!!errors.status} helperText={errors.status?.message}>
                        <MenuItem value="ACTIVE">Active</MenuItem>
                        <MenuItem value="INACTIVE">Inactive</MenuItem>
                      </TextField>
                    )}
                  />
                </>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ justifyContent: "end", pb: 2, mt: 2 }}>
            <Button variant="outlined" color="secondary" onClick={handleCloseForm} sx={{ textTransform: "none" }}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" color="primary" sx={{ textTransform: "none" }}>
              Save
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default Programs;
